import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { buildBuildingAddressString } from '../../lib/formatters';
import { COUNTRIES } from '../../lib/countries';
import {
  PROPERTY_TYPE_CATALOG,
  applyTypeDefaults,
  buildingToForm,
  emptyBuildingForm,
  findCategory,
  hydrateType,
  findType,
} from '../../lib/propertyTypes';

const SECTION_META = {
  identity: { icon: 'fa-signature', title: 'Name' },
  address: { icon: 'fa-map-marker-alt', title: 'Address' },
  contact: { icon: 'fa-phone', title: 'Contact' },
  size: { icon: 'fa-ruler-combined', title: 'Size & structure' },
  occupancy: { icon: 'fa-door-open', title: 'Occupancy' },
  dwelling: { icon: 'fa-bed', title: 'Home details' },
  legal: { icon: 'fa-balance-scale', title: 'Legal & zoning' },
  commercial: { icon: 'fa-file-invoice-dollar', title: 'Leasing' },
  industrial: { icon: 'fa-warehouse', title: 'Industrial specs' },
  land: { icon: 'fa-map', title: 'Land' },
  parking: { icon: 'fa-parking', title: 'Parking' },
  systems: { icon: 'fa-cogs', title: 'Systems' },
  amenities: { icon: 'fa-star', title: 'Amenities' },
  security: { icon: 'fa-shield-alt', title: 'Security & access' },
  notes: { icon: 'fa-sticky-note', title: 'Notes' },
  map: { icon: 'fa-globe', title: 'Map' },
};

const EXTRA_SECTION = {
  bedrooms: 'dwelling',
  bathrooms: 'dwelling',
  lot_size_sqft: 'dwelling',
  garage: 'dwelling',
  basement: 'dwelling',
  ownership: 'legal',
  zoning: 'legal',
  pid: 'legal',
  strata_plan: 'legal',
  strata_fees: 'legal',
  additional_rent_psf: 'commercial',
  additional_rent_year: 'commercial',
  lease_style: 'commercial',
  hvac_hours: 'commercial',
  fibre_provider: 'commercial',
  frontage_ft: 'commercial',
  residential_units: 'occupancy',
  commercial_suites: 'occupancy',
  loading_docks: 'industrial',
  clear_height_ft: 'industrial',
  yard_area_sqft: 'industrial',
  power_service: 'industrial',
  lot_size_acres: 'land',
  services_at_lot: 'land',
};

function hasSection(type, id) {
  return Array.isArray(type?.sections) && type.sections.includes(id);
}

function FieldInput({ id, def, value, onChange }) {
  const kind = def?.kind || 'text';
  const common = {
    id,
    name: id,
    value: value ?? '',
    onChange,
  };
  if (kind === 'select') {
    return (
      <select {...common}>
        {(def.options || ['']).map((opt) => (
          <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
        ))}
      </select>
    );
  }
  if (kind === 'yesno') {
    return (
      <select {...common}>
        <option value="">Select…</option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    );
  }
  if (kind === 'textarea') {
    return <textarea {...common} rows={def.rows || 4} placeholder={def.placeholder} />;
  }
  return (
    <input
      {...common}
      type={kind === 'number' ? 'number' : 'text'}
      placeholder={def.placeholder}
      min={def.min}
      step={def.step}
    />
  );
}

function BuildingModal({
  building,
  isOpen,
  onClose,
  onSave,
  isNewBuilding = false,
  companyId = null,
  companyName = '',
}) {
  const [catalog, setCatalog] = useState(PROPERTY_TYPE_CATALOG);
  const [editedBuilding, setEditedBuilding] = useState(() => emptyBuildingForm(companyId));
  const [categoryFilter, setCategoryFilter] = useState('multifamily');
  const [showTypePicker, setShowTypePicker] = useState(true);
  const [showCustomType, setShowCustomType] = useState(false);
  const [customLabel, setCustomLabel] = useState('');
  const [customCategory, setCustomCategory] = useState('other');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    fetch('/api/property-types')
      .then((res) => (res.ok ? res.json() : PROPERTY_TYPE_CATALOG))
      .then((data) => {
        if (data?.types?.length) setCatalog(data);
      })
      .catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const form = isNewBuilding || !building
      ? emptyBuildingForm(companyId)
      : buildingToForm(building, companyId, catalog);
    setEditedBuilding(form);
    setShowTypePicker(!form.property_type_id);
    setError('');
    setShowCustomType(false);
    if (form.property_category) setCategoryFilter(form.property_category);
  }, [building, isNewBuilding, companyId, isOpen]);

  const selectedType = useMemo(
    () => hydrateType(catalog, findType(catalog, editedBuilding.property_type_id)),
    [catalog, editedBuilding.property_type_id]
  );

  const typesInCategory = useMemo(
    () => (catalog.types || []).filter((t) => t.category === categoryFilter),
    [catalog, categoryFilter]
  );

  const fieldDefs = catalog.fieldDefs || PROPERTY_TYPE_CATALOG.fieldDefs;
  const amenityDefs = catalog.amenityDefs || PROPERTY_TYPE_CATALOG.amenityDefs;
  const selectOptions = catalog.selectOptions || PROPERTY_TYPE_CATALOG.selectOptions;

  const extraBySection = useMemo(() => {
    const grouped = {};
    for (const key of selectedType?.extraFields || []) {
      const preferred = EXTRA_SECTION[key] || 'legal';
      const section = hasSection(selectedType, preferred)
        ? preferred
        : (hasSection(selectedType, 'land') ? 'land' : (hasSection(selectedType, 'legal') ? 'legal' : preferred));
      if (!grouped[section]) grouped[section] = [];
      grouped[section].push(key);
    }
    return grouped;
  }, [selectedType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedBuilding((prev) => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (e) => {
    const { name, value } = e.target;
    setEditedBuilding((prev) => ({
      ...prev,
      type_details: { ...(prev.type_details || {}), [name]: value },
    }));
  };

  const handleCountryChange = (e) => {
    const countryName = e.target.value;
    const selectedCountry = COUNTRIES.find((c) => c.name === countryName);
    if (selectedCountry && selectedCountry.code === 'DIVIDER') return;
    setEditedBuilding((prev) => ({
      ...prev,
      country: countryName,
      country_code: selectedCountry ? selectedCountry.dialCode : '',
    }));
  };

  const selectType = (type) => {
    const hydrated = hydrateType(catalog, type);
    setEditedBuilding((prev) => applyTypeDefaults(prev, hydrated));
    setShowTypePicker(false);
    setShowCustomType(false);
  };

  const addCustomType = async () => {
    if (!customLabel.trim()) {
      setError('Enter a name for the new property type.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const response = await fetch('/api/property-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: customCategory, label: customLabel.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not add property type');
      const nextCatalog = {
        ...catalog,
        types: [...(catalog.types || []), data.type],
      };
      setCatalog(nextCatalog);
      setCategoryFilter(data.type.category);
      selectType(data.type);
      setCustomLabel('');
    } catch (err) {
      setError(err.message || 'Could not add property type.');
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddress = async () => {
    const { street_number, street_name, city, country } = editedBuilding;
    if (!street_name || !city || !country) {
      setError('Fill in street name, city, and country to find coordinates.');
      return;
    }
    try {
      const parts = [];
      if (street_number && street_name) parts.push(`${street_number} ${street_name}`);
      else if (street_name) parts.push(street_name);
      if (city) parts.push(city);
      if (country) parts.push(country);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(parts.join(', '))}&limit=1`
      );
      const data = await response.json();
      if (data && data.length > 0) {
        setEditedBuilding((prev) => ({
          ...prev,
          latitude: parseFloat(data[0].lat).toFixed(6),
          longitude: parseFloat(data[0].lon).toFixed(6),
        }));
        setError('');
      } else {
        setError('Address not found. Check the address or enter coordinates manually.');
      }
    } catch (err) {
      setError('Could not look up coordinates. Enter them manually if needed.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editedBuilding.property_type_id) {
      setError('Choose a property type first.');
      setShowTypePicker(true);
      return;
    }
    if (!editedBuilding.name || !editedBuilding.street_name || !editedBuilding.city || !editedBuilding.country || !editedBuilding.phone_number) {
      setError('Please fill in name, street, city, country, and phone number.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const url = isNewBuilding ? '/api/buildings' : `/api/buildings/${building.id}`;
      const payload = {
        ...editedBuilding,
        company_id: editedBuilding.company_id || companyId || null,
        address: buildBuildingAddressString(editedBuilding),
        type_details: editedBuilding.type_details || {},
      };
      const response = await fetch(url, {
        method: isNewBuilding ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save building');
      }
      const result = await response.json();
      onSave(result.building || editedBuilding);
    } catch (err) {
      setError(err.message || 'Failed to save building.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const occupancyPlural = selectedType?.occupancyNounPlural || 'units';
  const showSystemsLaundry = hasSection(selectedType, 'systems')
    && !(selectedType?.amenities || []).includes('laundry_facilities');

  const renderExtras = (sectionId) => (
    (extraBySection[sectionId] || []).map((key) => {
      const def = fieldDefs[key];
      if (!def) return null;
      return (
        <div className="form-group" key={key}>
          <label htmlFor={`detail_${key}`}>{def.label}</label>
          <FieldInput
            id={key}
            def={def}
            value={(editedBuilding.type_details || {})[key] || ''}
            onChange={handleDetailChange}
          />
        </div>
      );
    })
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content building-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3>{isNewBuilding ? 'Add property' : 'Edit property'}</h3>
            {companyName ? <p className="building-modal-enterprise">{companyName}</p> : null}
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>

        {showTypePicker ? (
          <div className="modal-body building-type-picker">
            {error ? <div className="error-message">{error}</div> : null}
            <p className="building-type-lead">
              Choose the kind of real estate. The form then shows only the details that type needs.
            </p>
            <div className="building-category-tabs" role="tablist">
              {(catalog.categories || []).map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  role="tab"
                  aria-selected={categoryFilter === cat.id}
                  className={`building-category-tab${categoryFilter === cat.id ? ' is-active' : ''}`}
                  onClick={() => setCategoryFilter(cat.id)}
                >
                  <i className={`fas ${cat.icon}`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
            <p className="building-category-desc">
              {findCategory(catalog, categoryFilter)?.description}
            </p>
            <div className="building-type-grid">
              {typesInCategory.map((type) => {
                const hydrated = hydrateType(catalog, type);
                const selected = editedBuilding.property_type_id === type.id;
                return (
                  <button
                    key={type.id}
                    type="button"
                    className={`building-type-card${selected ? ' is-selected' : ''}`}
                    onClick={() => selectType(type)}
                  >
                    <strong>{hydrated.label}</strong>
                    <span>{hydrated.occupancyNounPlural}</span>
                  </button>
                );
              })}
            </div>
            {showCustomType ? (
              <div className="building-custom-type">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="custom_category">Category</label>
                    <select
                      id="custom_category"
                      value={customCategory}
                      onChange={(e) => setCustomCategory(e.target.value)}
                    >
                      {(catalog.categories || []).map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="custom_label">New type name</label>
                    <input
                      id="custom_label"
                      value={customLabel}
                      onChange={(e) => setCustomLabel(e.target.value)}
                      placeholder="e.g., Medical clinic, Self-storage"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <button type="button" className="btn btn-primary" onClick={addCustomType} disabled={loading}>
                    Add type and continue
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCustomType(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="building-add-type-link" onClick={() => setShowCustomType(true)}>
                <i className="fas fa-plus"></i> Add a new property type
              </button>
            )}
            {editedBuilding.property_type_id ? (
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTypePicker(false)}>
                  Back to form
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="modal-body">
            {error ? <div className="error-message" style={{ marginBottom: 16 }}>{error}</div> : null}

            {selectedType ? (
              <div className="building-type-chip-row">
                <div className="building-type-chip">
                  <i className={`fas ${selectedType.categoryIcon || 'fa-building'}`}></i>
                  <div>
                    <strong>{selectedType.label}</strong>
                    <span>{selectedType.categoryLabel}</span>
                  </div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowTypePicker(true)}>
                  Change type
                </button>
              </div>
            ) : null}
            {selectedType?.hint ? <p className="building-type-hint">{selectedType.hint}</p> : null}

            {hasSection(selectedType, 'identity') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.identity.icon}`}></i> {SECTION_META.identity.title}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Property name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={editedBuilding.name}
                      onChange={handleChange}
                      required
                      placeholder={selectedType?.namePlaceholder || 'e.g., Chatham House'}
                    />
                  </div>
                  {selectedType?.hideBuildingName ? null : (
                    <div className="form-group">
                      <label htmlFor="building_name">Building / wing (if applicable)</label>
                      <input
                        type="text"
                        id="building_name"
                        name="building_name"
                        value={editedBuilding.building_name}
                        onChange={handleChange}
                        placeholder="e.g., Tower A, North Wing"
                      />
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'address') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.address.icon}`}></i> Address</h4>
                <div className="form-row">
                  <div className="form-group" style={{ flex: '0 0 120px' }}>
                    <label htmlFor="street_number">Number</label>
                    <input type="text" id="street_number" name="street_number" value={editedBuilding.street_number} onChange={handleChange} placeholder="8120" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="street_name">Street *</label>
                    <input type="text" id="street_name" name="street_name" value={editedBuilding.street_name} onChange={handleChange} required placeholder="Granville Ave" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input type="text" id="city" name="city" value={editedBuilding.city} onChange={handleChange} required placeholder="Richmond" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="postal_code">Postal / ZIP</label>
                    <input type="text" id="postal_code" name="postal_code" value={editedBuilding.postal_code} onChange={handleChange} placeholder="V6Y 1P3" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="country">Country *</label>
                    <select id="country" name="country" value={editedBuilding.country} onChange={handleCountryChange} required>
                      <option value="">Select country</option>
                      {COUNTRIES.map((country) => (
                        <option
                          key={country.code}
                          value={country.name}
                          disabled={country.code === 'DIVIDER'}
                        >
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="country_code">Dialing code</label>
                    <input type="text" id="country_code" name="country_code" value={editedBuilding.country_code} readOnly />
                  </div>
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'contact') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.contact.icon}`}></i> Contact</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone_number">Phone *</label>
                    <input type="tel" id="phone_number" name="phone_number" value={editedBuilding.phone_number} onChange={handleChange} required placeholder="604-447-8313" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input type="email" id="email" name="email" value={editedBuilding.email} onChange={handleChange} placeholder="info@optimumrealty.ca" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="web_address">Website</label>
                    <input type="url" id="web_address" name="web_address" value={editedBuilding.web_address} onChange={handleChange} placeholder="https://www.optimumrealty.ca" />
                  </div>
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'size') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.size.icon}`}></i> Size & structure</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="year_built">Year built</label>
                    <input type="number" id="year_built" name="year_built" value={editedBuilding.year_built || ''} onChange={handleChange} placeholder="e.g., 1985" min="1800" max={new Date().getFullYear()} />
                  </div>
                  <div className="form-group">
                    <label htmlFor="number_of_floors">Storeys / floors</label>
                    <input type="number" id="number_of_floors" name="number_of_floors" value={editedBuilding.number_of_floors || ''} onChange={handleChange} placeholder="e.g., 3" min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="total_floor_area">Floor area (sq ft)</label>
                    <input type="number" id="total_floor_area" name="total_floor_area" value={editedBuilding.total_floor_area || ''} onChange={handleChange} placeholder="e.g., 19000" min="0" />
                  </div>
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'occupancy') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.occupancy.icon}`}></i> {occupancyPlural.charAt(0).toUpperCase() + occupancyPlural.slice(1)}</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="total_units">Total {occupancyPlural}</label>
                    <input type="number" id="total_units" name="total_units" value={editedBuilding.total_units || ''} onChange={handleChange} placeholder="e.g., 12" min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="occupied_units">Occupied {occupancyPlural}</label>
                    <input type="number" id="occupied_units" name="occupied_units" value={editedBuilding.occupied_units || ''} onChange={handleChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="vacant_units">Vacant {occupancyPlural}</label>
                    <input type="number" id="vacant_units" name="vacant_units" value={editedBuilding.vacant_units || ''} onChange={handleChange} min="0" />
                  </div>
                </div>
                {extraBySection.occupancy?.length ? <div className="form-row">{renderExtras('occupancy')}</div> : null}
              </div>
            ) : null}

            {hasSection(selectedType, 'dwelling') && extraBySection.dwelling?.length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.dwelling.icon}`}></i> Home details</h4>
                <div className="form-row">{renderExtras('dwelling')}</div>
              </div>
            ) : null}

            {hasSection(selectedType, 'legal') && extraBySection.legal?.length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.legal.icon}`}></i> Legal & zoning</h4>
                <div className="form-row">{renderExtras('legal')}</div>
              </div>
            ) : null}

            {hasSection(selectedType, 'commercial') && extraBySection.commercial?.length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.commercial.icon}`}></i> Leasing</h4>
                <div className="form-row">{renderExtras('commercial')}</div>
              </div>
            ) : null}

            {hasSection(selectedType, 'industrial') && extraBySection.industrial?.length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.industrial.icon}`}></i> Industrial specs</h4>
                <div className="form-row">{renderExtras('industrial')}</div>
              </div>
            ) : null}

            {hasSection(selectedType, 'land') && extraBySection.land?.length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.land.icon}`}></i> Land</h4>
                <div className="form-row">{renderExtras('land')}</div>
              </div>
            ) : null}

            {hasSection(selectedType, 'parking') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.parking.icon}`}></i> Parking</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="total_parking">Total stalls</label>
                    <input type="number" id="total_parking" name="total_parking" value={editedBuilding.total_parking || ''} onChange={handleChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="visitor_parking">Visitor</label>
                    <input type="number" id="visitor_parking" name="visitor_parking" value={editedBuilding.visitor_parking || ''} onChange={handleChange} min="0" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="covered_parking">Covered</label>
                    <input type="number" id="covered_parking" name="covered_parking" value={editedBuilding.covered_parking || ''} onChange={handleChange} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="parking_type">Parking type</label>
                    <select id="parking_type" name="parking_type" value={editedBuilding.parking_type || ''} onChange={handleChange}>
                      {(selectOptions.parking_type || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="ev_charging">EV chargers</label>
                    <input type="number" id="ev_charging" name="ev_charging" value={editedBuilding.ev_charging || ''} onChange={handleChange} min="0" />
                  </div>
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'systems') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.systems.icon}`}></i> Systems</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="heating_system">Heating</label>
                    <select id="heating_system" name="heating_system" value={editedBuilding.heating_system || ''} onChange={handleChange}>
                      {(selectOptions.heating_system || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cooling_system">Cooling</label>
                    <select id="cooling_system" name="cooling_system" value={editedBuilding.cooling_system || ''} onChange={handleChange}>
                      {(selectOptions.cooling_system || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="elevators">Elevators</label>
                    <input type="number" id="elevators" name="elevators" value={editedBuilding.elevators || ''} onChange={handleChange} min="0" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="water_heater">Water heater</label>
                    <select id="water_heater" name="water_heater" value={editedBuilding.water_heater || ''} onChange={handleChange}>
                      {(selectOptions.water_heater || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  {showSystemsLaundry ? (
                    <div className="form-group">
                      <label htmlFor="laundry_facilities">Laundry</label>
                      <select id="laundry_facilities" name="laundry_facilities" value={editedBuilding.laundry_facilities || ''} onChange={handleChange}>
                        {(amenityDefs.laundry_facilities?.options || ['']).map((opt) => (
                          <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'amenities') && (selectedType.amenities || []).length ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.amenities.icon}`}></i> Amenities</h4>
                <div className="form-row">
                  {selectedType.amenities.map((key) => {
                    const def = amenityDefs[key];
                    if (!def) return null;
                    return (
                      <div className="form-group" key={key}>
                        <label htmlFor={key}>{def.label}</label>
                        <FieldInput id={key} def={def} value={editedBuilding[key] || ''} onChange={handleChange} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'security') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.security.icon}`}></i> Security & access</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="security_system">Security</label>
                    <select id="security_system" name="security_system" value={editedBuilding.security_system || ''} onChange={handleChange}>
                      {(selectOptions.security_system || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="access_control">Access</label>
                    <select id="access_control" name="access_control" value={editedBuilding.access_control || ''} onChange={handleChange}>
                      {(selectOptions.access_control || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="concierge">Concierge</label>
                    <select id="concierge" name="concierge" value={editedBuilding.concierge || ''} onChange={handleChange}>
                      {(selectOptions.concierge || []).map((opt) => (
                        <option key={opt || 'blank'} value={opt}>{opt || 'Select…'}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'notes') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.notes.icon}`}></i> Notes</h4>
                <div className="form-group full-width">
                  <label htmlFor="additional_notes">Special features & notes</label>
                  <textarea
                    id="additional_notes"
                    name="additional_notes"
                    value={editedBuilding.additional_notes || ''}
                    onChange={handleChange}
                    rows="5"
                    placeholder="Anything else needed to operate or list this property…"
                  />
                </div>
              </div>
            ) : null}

            {hasSection(selectedType, 'map') ? (
              <div className="form-section">
                <h4><i className={`fas ${SECTION_META.map.icon}`}></i> Map</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="latitude">Latitude</label>
                    <input type="number" step="any" id="latitude" name="latitude" value={editedBuilding.latitude} onChange={handleChange} placeholder="49.163" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="longitude">Longitude</label>
                    <input type="number" step="any" id="longitude" name="longitude" value={editedBuilding.longitude} onChange={handleChange} placeholder="-123.136" />
                  </div>
                </div>
                <div className="form-row" style={{ marginTop: 12, gap: 10 }}>
                  <button type="button" className="btn btn-secondary" onClick={geocodeAddress}>
                    Find coordinates from address
                  </button>
                  {editedBuilding.latitude && editedBuilding.longitude ? (
                    <a
                      href={`https://www.google.com/maps?q=${editedBuilding.latitude},${editedBuilding.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      View on Google Maps
                    </a>
                  ) : null}
                </div>
                {editedBuilding.latitude && editedBuilding.longitude ? (
                  <div style={{ height: 280, width: '100%', marginTop: 12, borderRadius: 8, overflow: 'hidden' }}>
                    <MapContainer
                      center={[parseFloat(editedBuilding.latitude), parseFloat(editedBuilding.longitude)]}
                      zoom={15}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      />
                      <Marker position={[parseFloat(editedBuilding.latitude), parseFloat(editedBuilding.longitude)]}>
                        <Popup>
                          <strong>{editedBuilding.name || 'Property location'}</strong>
                        </Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving…' : (isNewBuilding ? 'Add property' : 'Save changes')}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default BuildingModal;
